'use client';

import clsx from 'clsx';
import { createElement, Fragment, useContext, type ReactNode } from 'react';
import { Button as RACButton, VisuallyHidden as RACVisuallyHidden } from 'react-aria-components';
import { IconCheckBold, IconCircleBoldFill } from '../../icons/index.js';
import type { CommonProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';
import { StepContext } from './context.js';
import type { StepperStatus } from './types.js';

// TODO: move to translations
const assistiveTextMap: Record<StepperStatus, string> = {
  incomplete: 'Incomplete',
  complete: 'Completed',
  error: 'Invalid',
  warning: 'Warning',
  'in-progress': 'In progress',
};

const statusIconMap: Record<StepperStatus, React.ElementType> = {
  incomplete: Fragment,
  complete: IconCheckBold,
  // TODO: replace with icon from library when available
  error: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M24.1421 21.1421C24.9232 21.9232 24.9232 23.1895 24.1421 23.9706C23.3611 24.7516 22.0948 24.7516 21.3137 23.9706L8.58579 11.2426C7.80474 10.4616 7.80474 9.19526 8.58579 8.41421C9.36684 7.63316 10.6332 7.63316 11.4142 8.41421L24.1421 21.1421Z"
        fill="currentColor"
      />
      <path
        d="M21.1421 8.85786C21.9232 8.07682 23.1895 8.07682 23.9706 8.85786C24.7516 9.63891 24.7516 10.9052 23.9706 11.6863L11.2426 24.4142C10.4616 25.1953 9.19526 25.1953 8.41421 24.4142C7.63316 23.6332 7.63316 22.3668 8.41421 21.5858L21.1421 8.85786Z"
        fill="currentColor"
      />
    </svg>
  ),
  // TODO: replace with icon from library when available
  warning: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 24C18 25.1046 17.1046 26 16 26C14.8954 26 14 25.1046 14 24C14 22.8954 14.8954 22 16 22C17.1046 22 18 22.8954 18 24Z"
        fill="currentColor"
      />
      <line x1="16" y1="8" x2="16" y2="18" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  ),
  'in-progress': IconCircleBoldFill,
};

export interface UNSTABLE_StepperItemProps extends CommonProps {
  /** The title of the step. */
  children: StringLikeChildren;
  /** The description of the step. Optionally, you can pass a React node to render rich text. */
  description?: ReactNode;
  /** The status of the step. */
  status?: StepperStatus;
}

/**
 * Represents a single step in a stepper component.
 */
export function UNSTABLE_StepperItem({
  children,
  description,
  status: overrideStatus,
  UNSAFE_className,
  UNSAFE_style,
  ...props
}: UNSTABLE_StepperItemProps) {
  const { currentStep, stepIndex, onChange, stepCount, orientation } = useContext(StepContext);
  const isCurrent = stepIndex === currentStep;
  const isPrevious = stepIndex < currentStep;
  const isLast = stepIndex === stepCount - 1;
  const status = overrideStatus ?? (isCurrent ? 'in-progress' : isPrevious ? 'complete' : 'incomplete');
  const isNavigable = !isCurrent && onChange != null && (overrideStatus != null || isPrevious);

  return (
    <div
      {...props}
      role="listitem"
      className={clsx('cim-stepper-item', UNSAFE_className)}
      style={UNSAFE_style}
      data-status={status}
      aria-current={isCurrent}
    >
      <div className="cim-stepper-item-icon-container" aria-hidden>
        <StepperItemIndicator status={status} isNavigable={isNavigable} isCurrent={isCurrent} onChange={onChange} />
        {!isLast && orientation === 'vertical' && <div className="cim-stepper-item-tail" />}
      </div>
      <div className="cim-stepper-item-content">
        <StepperItemTitle status={status} isNavigable={isNavigable} isCurrent={isCurrent} onChange={onChange}>
          {children}
        </StepperItemTitle>
        {description && (
          <Text as="div" UNSAFE_className="cim-stepper-item-description" variant="medium" tone="muted">
            {description}
          </Text>
        )}
      </div>
    </div>
  );
}

function StepperItemIndicator({
  status,
  isNavigable,
  isCurrent,
  onChange,
}: {
  status: StepperStatus;
  isNavigable: boolean;
  isCurrent: boolean;
  onChange?: () => void;
}) {
  const icon = (
    <div className="cim-stepper-item-icon">
      {status === 'in-progress' && !isCurrent ? null : createElement(statusIconMap[status])}
    </div>
  );

  if (isNavigable) {
    return (
      <RACButton className="cim-stepper-item-icon-button" type="button" onClick={onChange} excludeFromTabOrder>
        {icon}
      </RACButton>
    );
  }

  return <div className="cim-stepper-item-icon-button">{icon}</div>;
}

function StepperItemTitle({
  children,
  status,
  isNavigable,
  isCurrent,
  onChange,
}: {
  children: StringLikeChildren;
  status: StepperStatus;
  isNavigable: boolean;
  isCurrent: boolean;
  onChange?: () => void;
}) {
  const assistiveText = assistiveTextMap[status];

  const titleElement = (
    <Text as="span" variant={isCurrent ? 'body-semibold' : 'body'} UNSAFE_className="cim-stepper-item-title">
      <RACVisuallyHidden>{assistiveText}: </RACVisuallyHidden>
      {children}
    </Text>
  );

  return isNavigable ? (
    <RACButton className="cim-stepper-item-title-button" type="button" onClick={onChange}>
      {titleElement}
    </RACButton>
  ) : (
    <div className="cim-stepper-item-title-button">{titleElement}</div>
  );
}
